import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

/**
 * TourImpressao — "tour guiado" (coach marks) para apresentar novidades do modal.
 *
 * Como funciona: escurece a tela inteira, deixa um "buraco" de luz sobre o elemento
 * em destaque e mostra um balão com título, texto e botões (Voltar / Próximo / Concluir
 * / Pular). Avança passo a passo. Quem decide SE deve aparecer (e grava no cache que já
 * foi visto) é a página — este componente só exibe e avisa quando termina (aoFechar).
 *
 * Posicionamento: usamos getBoundingClientRect do alvo, que JÁ reflete qualquer redução
 * (transform: scale) do modal — então o destaque encaixa certinho mesmo com o modal
 * reduzido para caber na tela. O tour é desenhado por cima de tudo (z alto), fora do modal.
 *
 * Componente isolado e "só leitura": não toca impressão, saldo, fontes nem o conteúdo do
 * modal. Entregável para o programador encaixar no projeto real.
 */

export interface PassoTour {
  /** Seletor CSS do elemento a destacar, ex.: '[data-tour="coluna-modificar"]'. */
  seletor: string;
  titulo: string;
  texto: string;
}

interface TourImpressaoProps {
  passos: PassoTour[];
  /** Chamado ao Concluir, Pular ou se o alvo sumir. A página marca como visto e esconde. */
  aoFechar: () => void;
}

const PADDING_FOCO = 8;    // respiro (px) ao redor do elemento destacado
const ESPACO_BALAO = 14;   // distância (px) entre o balão e o elemento
const LARGURA_BALAO = 320; // largura do balão (px)
const MARGEM_TELA = 8;     // respiro mínimo (px) das bordas da janela

type Lado = 'baixo' | 'cima' | 'direita' | 'esquerda';

const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v));

export const TourImpressao: React.FC<TourImpressaoProps> = ({ passos, aoFechar }) => {
  const [indice, setIndice] = useState(0);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const [balaoH, setBalaoH] = useState(0);
  const balaoRef = useRef<HTMLDivElement>(null);

  // Mantém a última versão de aoFechar sem forçar o efeito de medição a re-rodar.
  const aoFecharRef = useRef(aoFechar);
  useEffect(() => { aoFecharRef.current = aoFechar; });

  // Esc encerra SÓ o tour (sem fechar o modal). Captura antes do listener do modal e
  // interrompe a propagação, para o modal não fechar junto.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopImmediatePropagation();
        aoFecharRef.current();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, []);

  const passo = passos[indice];
  const ultimo = indice === passos.length - 1;
  const primeiro = indice === 0;

  // Mede a posição do alvo na tela (em coordenadas da janela; já reflete o scale do modal).
  // Re-mede quando muda de passo, quando a janela muda de tamanho/zoom e ao rolar a página.
  useLayoutEffect(() => {
    let raf = 0;
    const medir = () => {
      if (!passo) return;
      const alvo = document.querySelector(passo.seletor) as HTMLElement | null;
      if (!alvo) { aoFecharRef.current(); return; } // alvo sumiu: encerra com segurança
      setRect(alvo.getBoundingClientRect());
    };
    medir();
    // Re-mede no próximo quadro, depois do layout assentar (modal já reduzido, se for o caso).
    raf = requestAnimationFrame(medir);
    window.addEventListener('resize', medir);
    window.addEventListener('scroll', medir, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', medir);
      window.removeEventListener('scroll', medir, true);
    };
  }, [indice, passo]);

  // Mede a altura real do balão (depende do texto) para posicioná-lo sem cortar.
  useLayoutEffect(() => {
    if (balaoRef.current) setBalaoH(balaoRef.current.offsetHeight);
  }, [indice, rect]);

  if (!passo || !rect) return null;

  const vw = window.innerWidth;
  const vh = window.innerHeight;

  // "Buraco" de luz: o retângulo do alvo com um respiro ao redor.
  const foco = {
    top: rect.top - PADDING_FOCO,
    left: rect.left - PADDING_FOCO,
    width: rect.width + PADDING_FOCO * 2,
    height: rect.height + PADDING_FOCO * 2,
  };

  // Escolhe de que lado do alvo o balão cabe melhor (baixo > cima > direita > esquerda).
  const precisoVertical = balaoH + ESPACO_BALAO + MARGEM_TELA;
  const precisoHorizontal = LARGURA_BALAO + ESPACO_BALAO + MARGEM_TELA;
  let lado: Lado = 'baixo';
  if (vh - rect.bottom >= precisoVertical) lado = 'baixo';
  else if (rect.top >= precisoVertical) lado = 'cima';
  else if (vw - rect.right >= precisoHorizontal) lado = 'direita';
  else if (rect.left >= precisoHorizontal) lado = 'esquerda';

  const alvoCentroX = rect.left + rect.width / 2;
  const alvoCentroY = rect.top + rect.height / 2;

  // Posição (top/left) do balão conforme o lado escolhido, presa dentro da janela.
  let balaoTop: number;
  let balaoLeft: number;
  if (lado === 'baixo') {
    balaoTop = rect.bottom + ESPACO_BALAO;
    balaoLeft = alvoCentroX - LARGURA_BALAO / 2;
  } else if (lado === 'cima') {
    balaoTop = rect.top - ESPACO_BALAO - balaoH;
    balaoLeft = alvoCentroX - LARGURA_BALAO / 2;
  } else if (lado === 'direita') {
    balaoTop = alvoCentroY - balaoH / 2;
    balaoLeft = rect.right + ESPACO_BALAO;
  } else {
    balaoTop = alvoCentroY - balaoH / 2;
    balaoLeft = rect.left - ESPACO_BALAO - LARGURA_BALAO;
  }
  balaoLeft = clamp(balaoLeft, MARGEM_TELA, vw - LARGURA_BALAO - MARGEM_TELA);
  balaoTop = clamp(balaoTop, MARGEM_TELA, Math.max(MARGEM_TELA, vh - balaoH - MARGEM_TELA));

  // Posição da setinha (diamante) na borda do balão, alinhada ao centro do alvo.
  const seta: React.CSSProperties = { position: 'absolute', width: 12, height: 12, background: '#fff', transform: 'rotate(45deg)' };
  if (lado === 'baixo') {
    seta.top = -6;
    seta.left = clamp(alvoCentroX - balaoLeft, 18, LARGURA_BALAO - 18) - 6;
    seta.boxShadow = '-2px -2px 4px rgba(0,0,0,0.06)';
  } else if (lado === 'cima') {
    seta.bottom = -6;
    seta.left = clamp(alvoCentroX - balaoLeft, 18, LARGURA_BALAO - 18) - 6;
    seta.boxShadow = '2px 2px 4px rgba(0,0,0,0.06)';
  } else if (lado === 'direita') {
    seta.left = -6;
    seta.top = clamp(alvoCentroY - balaoTop, 18, Math.max(18, balaoH - 18)) - 6;
    seta.boxShadow = '-2px 2px 4px rgba(0,0,0,0.06)';
  } else {
    seta.right = -6;
    seta.top = clamp(alvoCentroY - balaoTop, 18, Math.max(18, balaoH - 18)) - 6;
    seta.boxShadow = '2px -2px 4px rgba(0,0,0,0.06)';
  }

  const avancar = () => { if (ultimo) aoFechar(); else setIndice((i) => i + 1); };
  const voltar = () => setIndice((i) => Math.max(0, i - 1));

  return (
    <div className="fixed inset-0 z-[60]">
      {/* Camada que bloqueia cliques na página enquanto o tour está ativo (transparente). */}
      <div
        className="absolute inset-0"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      />

      {/* Destaque (spotlight): o box-shadow gigante escurece TUDO, menos este retângulo. */}
      <div
        className="pointer-events-none fixed transition-all duration-300 ease-out"
        style={{
          top: foco.top,
          left: foco.left,
          width: foco.width,
          height: foco.height,
          borderRadius: 10,
          boxShadow: '0 0 0 99999px rgba(17,24,39,0.6)',
          outline: '2px solid rgba(59,130,246,0.9)',
          outlineOffset: 2,
        }}
      />

      {/* Balão de instrução. */}
      <div
        ref={balaoRef}
        className="fixed bg-white rounded-lg shadow-xl p-4 transition-all duration-300 ease-out"
        style={{ top: balaoTop, left: balaoLeft, width: LARGURA_BALAO }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={seta} aria-hidden="true" />

        <div className="flex items-center justify-between mb-1">
          <span className="text-xs font-medium text-blue-600">
            Passo {indice + 1} de {passos.length}
          </span>
          <button
            onClick={aoFechar}
            className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
          >
            Pular
          </button>
        </div>

        <h3 className="text-base font-bold text-gray-800 mb-1">{passo.titulo}</h3>
        <p className="text-sm text-gray-600 leading-relaxed mb-4">{passo.texto}</p>

        <div className="flex items-center justify-between">
          {/* Bolinhas de progresso */}
          <div className="flex items-center gap-1.5">
            {passos.map((_, i) => (
              <span
                key={i}
                className={`h-1.5 rounded-full transition-all ${i === indice ? 'w-4 bg-blue-600' : 'w-1.5 bg-gray-300'}`}
              />
            ))}
          </div>

          <div className="flex items-center gap-2">
            {!primeiro && (
              <button
                onClick={voltar}
                className="px-3 py-1.5 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-100 transition-colors"
              >
                Voltar
              </button>
            )}
            <button
              onClick={avancar}
              className="px-3 py-1.5 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700 transition-colors"
            >
              {ultimo ? 'Concluir' : 'Próximo'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TourImpressao;
